import { useEffect } from 'react'
import { Trans } from 'react-i18next'
import { useLocalStorage } from 'react-use'
import clsx from 'clsx'

import { Gallery16, Link16, Openai24, Unsplash24 } from '@makyoapp/makyo-icon/icon'
import { Modal, Tabs } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'
import {
  ImageManagerKind,
  ImageManagerProvider,
  ImageManagerProviderProps,
  useImageManagerContext,
} from '@/context/ImageManagerContext'
import { StorageProvider, useStorageContext } from '@/context/StorageContext'
import { STORAGE_KEYS } from '@/shared/constants'

import { ImageUrlPanel } from './ImageUrlPanel'
import { MyImagesPanel } from './MyImagesPanel'
import { UnsplashPanel } from './UnsplashPanel'

function ImageManagerNavs() {
  const { navLabels, onSelect } = useImageManagerContext()
  const [, setOpt] = useLocalStorage(STORAGE_KEYS.IMG_MGR_TAB, '', {
    raw: true,
  })

  return (
    <Tabs.Nav
      className='border-b bdr'
      onSelect={(eventKey) => {
        onSelect?.(eventKey as ImageManagerKind)
      }}
    >
      <Tabs.NavItem
        className='txt'
        eventKey={ImageManagerKind.MyImages}
        onClick={() => setOpt(ImageManagerKind.MyImages)}
      >
        <Gallery16 width={16} height={16} />
        <span>{navLabels?.myImages || <Trans>My Images</Trans>}</span>
      </Tabs.NavItem>
      <Tabs.NavItem
        className='txt'
        eventKey={ImageManagerKind.ImageUrl}
        onClick={() => setOpt(ImageManagerKind.ImageUrl)}
      >
        <Link16 />
        <span>{navLabels?.imageUrl || <Trans>Image URL</Trans>}</span>
      </Tabs.NavItem>
      <Tabs.NavItem
        className='txt'
        eventKey={ImageManagerKind.StockPhotos}
        onClick={() => setOpt(ImageManagerKind.StockPhotos)}
      >
        <Unsplash24 width={16} height={16} />
        <span>{navLabels?.stockPhotos || <Trans>Unsplash</Trans>}</span>
      </Tabs.NavItem>
      {/* Hide DALL-E as per ticket no: https://trello.com/c/hJeF93HL */}
      {/* <Tabs.NavItem className='txt' eventKey={ImageManagerKind.OpenAI} onClick={() => setOpt(ImageManagerKind.OpenAI)}>
        <Openai24 width={16} height={16} />
        <span>{navLabels?.openAI || <Trans>DALL-E</Trans>}</span>
      </Tabs.NavItem> */}
    </Tabs.Nav>
  )
}

function ImageManagerPanels() {
  const { maxHeight } = useImageManagerContext()
  const { setCurrentFolderId } = useStorageContext()

  useEffect(() => {
    return () => {
      setCurrentFolderId('')
    }
  }, [setCurrentFolderId])

  return (
    <div style={{ maxHeight: maxHeight ? maxHeight : '40vh', overflowY: 'auto' }}>
      <MyImagesPanel />
      <ImageUrlPanel />
      <UnsplashPanel />
      {/* <OpenAIPanel /> */}
    </div>
  )
}

function ImageManagerInner({ className }: { className?: string }) {
  const cachedKey = localStorage.getItem(STORAGE_KEYS.IMG_MGR_TAB)
  const activeKey =
    cachedKey && Object.values(ImageManagerKind).includes(cachedKey as ImageManagerKind)
      ? cachedKey
      : ImageManagerKind.MyImages

  return (
    <Tabs defaultActiveKey={activeKey} className={cn('mk-bg', className)}>
      <ImageManagerNavs />
      <ImageManagerPanels />
    </Tabs>
  )
}

function ImageManagerModalInner() {
  const { show, onHide, title, multiple, selectedImages } = useImageManagerContext()

  const len = selectedImages.length
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header className={clsx(multiple && 'h-auto!')} onCloseClick={onHide}>
        {title || multiple ? (
          selectedImages.length > 0 ? (
            <div className='flex flex-col gap-2 py-2'>
              <div>
                <Trans values={{ len }}>Select Images ({{ len }} selected)</Trans>
              </div>
              <div className='flex-c'>
                {selectedImages.map((f, i) => {
                  const isBlob = f.Url.startsWith('blob')
                  return (
                    <div key={i} className='flex-cc overflow-hidden w-[24px] h-[24px] object-contain'>
                      <img
                        src={f.Url}
                        onLoad={() => {
                          if (isBlob) {
                            URL.revokeObjectURL(f.Url)
                          }
                        }}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <Trans>Select Images</Trans>
          )
        ) : (
          <Trans>Select Image</Trans>
        )}
      </Modal.Header>
      <Modal.Body noPadding>
        <div className='modal-md'>
          <ImageManagerInner />
        </div>
      </Modal.Body>
    </Modal>
  )
}

export interface ImageManagerProps extends ImageManagerProviderProps {
  modal?: boolean
  innerClassname?: string
}

export function ImageManager({ modal = true, innerClassname, ...props }: ImageManagerProps) {
  return (
    <StorageProvider>
      <ImageManagerProvider {...props}>
        {modal ? <ImageManagerModalInner /> : <ImageManagerInner className={innerClassname} />}
      </ImageManagerProvider>
    </StorageProvider>
  )
}
